// Forecasts for a chart's in-progress period (USA-1734): a month that started three days ago
// otherwise reads as a collapse in the trend.

/** How far through the period [start, end) `now` is, from 0 to 1. */
export function elapsedShare(start: Date, end: Date, now = new Date()): number {
  const length = end.getTime() - start.getTime()
  if (length <= 0) return 1
  return Math.min(1, Math.max(0, (now.getTime() - start.getTime()) / length))
}

/**
 * Where the trend line through the recent full periods (oldest first) puts the next one; never
 * below zero, so a falling trend does not forecast a negative period.
 */
export function trendProjection(history: number[]): number {
  const count = history.length
  const meanX = (count - 1) / 2
  const meanY = history.reduce((sum, value) => sum + value, 0) / count
  let covariance = 0
  let variance = 0
  history.forEach((value, x) => {
    covariance += (x - meanX) * (value - meanY)
    variance += (x - meanX) ** 2
  })
  const slope = variance ? covariance / variance : 0
  return Math.max(0, meanY + slope * (count - meanX))
}

/**
 * The full-period figure for an in-progress period: what is in so far, plus the recent trend's
 * projection for the share still to come. Null once the period is over or with no history.
 */
export function forecastTotal(actual: number, history: number[], elapsed: number): number | null {
  if (elapsed >= 1 || history.length === 0) return null
  return actual + trendProjection(history) * (1 - elapsed)
}

/** The part of a forecast still to come, drawn stacked on the actual bar; never below zero. */
export function forecastRemainder(actual: number, forecast: number | null): number | undefined {
  return forecast == null ? undefined : Math.max(0, forecast - actual)
}

/** First day of the month after `day` (local dates). */
function nextMonth(day: Date) {
  return new Date(day.getFullYear(), day.getMonth() + 1, 1)
}

/** The whole calendar week or month a bucket starting on `start` belongs to, as [start, end). */
export function naturalPeriod(start: Date, unit: 'week' | 'month'): { start: Date; end: Date } {
  if (unit === 'month') {
    const first = new Date(start.getFullYear(), start.getMonth(), 1)
    return { start: first, end: nextMonth(first) }
  }
  // Weeks start on Monday, as the backend buckets them.
  const monday = new Date(start.getFullYear(), start.getMonth(), start.getDate() - ((start.getDay() + 6) % 7))
  return { start: monday, end: new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + 7) }
}

// Dashed outline over a light wash of the series color: a projection, not a measured figure.
export function forecastBarStyle(color: string) {
  return { fill: color, fillOpacity: 0.14, stroke: color, strokeWidth: 1.5, strokeDasharray: '4 3' }
}

/** How many full periods before the current one the trend line runs through. */
export const FORECAST_HISTORY = { week: 4, month: 2 } as const
